import FullCalendar from "@fullcalendar/react";
import BootstrapTheme from "@fullcalendar/bootstrap";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import { useTranslation } from "react-i18next";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import { getScheduleCalenderList } from "../../../redux/schedule/action";
import EventContent from "./EventContent";
import Loading from "../../Loading";
import { RootState } from "../../../redux";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { IRootState } from "../../../store";
import { Seg } from "@fullcalendar/core/internal";

interface CalendarViewProps {
  onDayClick: (date: Date) => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({ onDayClick }) => {
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { scheduleList = [], isLoading = false } = useAppSelector(
    (state: RootState) => state.schedule || {}
  );
  const calendarRef = useRef<FullCalendar | null>(null);
  const themeConfig = useSelector((state: IRootState) => state.themeConfig);
  const lastRangeRef = useRef<string | null>(null);
  const calendarContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
  const calendarEl = calendarContainerRef.current;
  if (!calendarEl) return;

  let startX: number | null = null;
  let startY: number | null = null;
  let isHorizontalSwipe: boolean | null = null;

  const handleTouchStart = (e: TouchEvent) => {
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
    isHorizontalSwipe = null;
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (startX === null || startY === null) return;

    const diffX = Math.abs(e.touches[0].clientX - startX);
    const diffY = Math.abs(e.touches[0].clientY - startY);

    // Lock direction on first meaningful move
    if (isHorizontalSwipe === null && (diffX > 10 || diffY > 10)) {
      isHorizontalSwipe = diffX > diffY;
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (startX === null || startY === null) return;
    if (!isHorizontalSwipe) return; // ignore vertical scrolls

    const diffX = e.changedTouches[0].clientX - startX;

    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    if (Math.abs(diffX) > 50) {
      if (diffX < 0) calendarApi.next(); // swipe left
      else calendarApi.prev(); // swipe right
    }

    startX = null;
    startY = null;
    isHorizontalSwipe = null;
  };

  calendarEl.addEventListener("touchstart", handleTouchStart, { passive: true });
  calendarEl.addEventListener("touchmove", handleTouchMove, { passive: true });
  calendarEl.addEventListener("touchend", handleTouchEnd);

  return () => {
    calendarEl.removeEventListener("touchstart", handleTouchStart);
    calendarEl.removeEventListener("touchmove", handleTouchMove);
    calendarEl.removeEventListener("touchend", handleTouchEnd);
  };
}, []);

  const handleDateClick = (info: any) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    const clickedDate = info.date;
    const viewStart = calendarApi.view.currentStart;

    const isSameMonth =
      clickedDate.getFullYear() === viewStart.getFullYear() &&
      clickedDate.getMonth() === viewStart.getMonth();

    // Ignore "+X more"
    const target = info.jsEvent.target as HTMLElement;
    if (target.closest(".fc-more-link")) return;

    if (!isSameMonth) {
      // Move calendar to the clicked date’s month
      calendarApi.gotoDate(clickedDate);
      return;
    }

    // Normal behavior for current month
    onDayClick(clickedDate);
  };

  useEffect(() => {
    let frame: number | null = null;
    const smoothResize = () => {
      calendarRef.current?.getApi()?.updateSize();
      frame = requestAnimationFrame(smoothResize);
    };

    if (themeConfig.sidebar !== undefined) {
      frame = requestAnimationFrame(smoothResize);

      // Stop updating after the transition duration
      setTimeout(() => {
        if (frame) cancelAnimationFrame(frame);
      }, 500); // match CSS transition duration
    }

    return () => {
      if (frame) cancelAnimationFrame(frame);
    };
  }, [themeConfig.sidebar]);

  interface Tab {
    label: string;
    action: () => void;
    active: boolean;
  }

  interface CustomToolbarProps {
    calendarApi: any; // can be undefined initially
  }

  const CustomToolbar: React.FC<CustomToolbarProps> = ({ calendarApi }) => {
    if (!calendarApi) return null; // don't render until calendarApi exists

    const currentStart = calendarApi.view.currentStart;
    const prevDate = new Date(currentStart);
    prevDate.setMonth(prevDate.getMonth() - 1);
    const nextDate = new Date(currentStart);
    nextDate.setMonth(nextDate.getMonth() + 1);

    const tabs = [
      {
        label: prevDate.toLocaleString("default", {
          month: "short",
          year: "numeric",
        }),
        action: () => calendarApi.prev(),
        active: false,
      },
      {
        label: currentStart.toLocaleString("default", {
          month: "long",
          year: "numeric",
        }),
        action: () => {},
        active: true,
      },
      {
        label: nextDate.toLocaleString("default", {
          month: "short",
          year: "numeric",
        }),
        action: () => calendarApi.next(),
        active: false,
      },
    ];

    return (
      <div className="grid grid-cols-3 w-full border border-b-0 overflow-hidden select-none">
        {tabs.map((tile, index) => (
          <div
            key={index}
            onClick={!tile.active ? tile.action : undefined}
            className={`
            py-3 text-center font-semibold transition 
            ${
              tile.active
                ? "bg-white text-black"
                : "bg-white hover:bg-green-100 text-gray-400 text-md cursor-pointer"
            }
          `}
          >
            {tile.label}
            <div className="flex justify-center w-full mt-1">
              <div
                className={`w-2 h-2 rounded-full mt-1 ${
                  tile.active ? "bg-green-300" : "bg-gray-300"
                }`}
              ></div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Function to call the API with a specific date
  const fetchSchedule = (startDate: Date) => {
    const payload = {
      date: startDate.toLocaleDateString("en-CA"), // Format the date as needed
      //   start: startDate.toLocaleDateString("en-CA"),
      //   end: endDate.toLocaleDateString("en-CA"),
      mode: "month", // You can adjust the mode if needed for other ranges
    };
    dispatch(getScheduleCalenderList(payload));
  };

  const transformApiResponse = (data: any) => {
    const list = Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data)
      ? data
      : [];

    const events: any[] = [];

    list.forEach((entry: any) => {
      // Sort courses by start_time
      const sortedCourses = [...entry.courses].sort((a, b) =>
        a.start_time.localeCompare(b.start_time)
      );

      // Assign sequential Class numbers per day
      sortedCourses.forEach((course: any, index: number) => {
        const start = new Date(`${entry.date}T${course.start_time}`);
        let end = new Date(`${entry.date}T${course.end_time}`);

        // Make overnight classes to same day so that it wont be display in next day
        if (end <= start) {
          end = new Date(start);
          end.setHours(23, 59, 59);
        }

        events.push({
          id: `${entry.date}-${String(index).padStart(3, "0")}`, // fix ordering
          title: `${course.classroom}`,
          start,
          end,
          allDay: false,
          extendedProps: {
            type: course.type,
            status: course.status,
            event_id: course.event_id,
          },
          eventOrder: index,
        });
      });
    });
    return events;
  };

  const handleDateRangeChange = (arg: any) => {
    const rangeKey = `${arg.startStr}_${arg.endStr}`;

    if (lastRangeRef.current === rangeKey) {
      return;
    }

    lastRangeRef.current = rangeKey;

    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    fetchSchedule(calendarApi.view.currentStart);
  };

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    const view = calendarApi.view;
    const currentMonth = view.currentStart;

    fetchSchedule(currentMonth);
  }, []);

  const handleMoreClick = (args: any) => {
    const dayEvents = args.allSegs.map((seg: Seg) => seg.event);
    onDayClick(args.date);
    return "none"; // prevent default popover
  };

  const handleEventClick = (clickInfo: any) => {
    const eventDate = clickInfo.event.start; // get the event's start date
    if (eventDate) {
      onDayClick(eventDate); // call the same function as handleMoreClick
    }
    clickInfo.jsEvent.preventDefault(); // prevent default popover
  };

  return (
    <>
      {isLoading && <Loading />}
      <div
        ref={calendarContainerRef}
        className="flex-1 transition-all duration-500 ease-in-out"
      >
        <CustomToolbar calendarApi={calendarRef?.current?.getApi()} />
        <FullCalendar
          ref={calendarRef}
          timeZone="local"
          plugins={[
            BootstrapTheme,
            dayGridPlugin,
            interactionPlugin,
            listPlugin,
          ]}
          initialView="dayGridMonth"
          dateClick={handleDateClick}
          themeSystem="bootstrap"
          eventDisplay={"block"}
          height="auto"
          locale={i18n.resolvedLanguage}
          headerToolbar={false}
          viewClassNames={"bg-[#FFFFFF] border border-0"}
          eventContent={EventContent}
          events={transformApiResponse(scheduleList)}
          eventOrder="eventOrder"
          dayMaxEvents={3}
          moreLinkClick={handleMoreClick}
          eventClick={handleEventClick}
          datesSet={handleDateRangeChange} // Triggered when the month changes
          eventClassNames={(arg) => {
            const status = arg.event.extendedProps.status;
            return ["mb-1", status ? `fc-status-${status.toLowerCase()}` : ""];
          }}
          dayHeaderContent={(args) => {
            const dayIndex = args.date.getDay();
            const dayLetter = args.date.toLocaleDateString("en-CA", {
              weekday: "narrow",
            });

            const style = {
              color: dayIndex === 0 || dayIndex === 6 ? "red" : "inherit",
              fontWeight: "bold",
            };
            return <span style={style}>{dayLetter}</span>;
          }}
        />
      </div>
    </>
  );
};

export default CalendarView;
